import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { PosTerminalsService } from 'src/app/services/pos-terminals.service';

@Component({
  selector: 'app-pos-terminal-form',
  templateUrl: './pos-terminal-form.component.html',
  styleUrls: ['./pos-terminal-form.component.scss'],
})
export class PosTerminalFormComponent implements OnInit {
  storeId: string = '';
  terminalId: string = '';
  isEditMode: boolean = false;

  terminalForm!: FormGroup;
  assignableUsers: any[] = [];
  originalAssignedUserId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private posTerminalsService: PosTerminalsService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.storeId = this.route.snapshot.paramMap.get('id') || '';
    this.terminalId = this.route.snapshot.paramMap.get('terminalId') || '';
    this.isEditMode = !!this.terminalId;

    this.initForm();
    this.loadFormData();
  }

  initForm(): void {
    this.terminalForm = new FormGroup({
      terminalName: new FormControl('', [Validators.required]),
      assignedUserId: new FormControl<string | null>(null),
      preferredScannerType: new FormControl('camera'),
      productScanQrMode: new FormControl('warn'),
      soundEnabled: new FormControl(true),
      hapticEnabled: new FormControl(true),
      autoLogoutMinutes: new FormControl(480),
      receiptPrinterEnabled: new FormControl(false),
      autoPrintReceiptEnabled: new FormControl(false),
      printerIP: new FormControl(null),
      preferredPaymentMethod: new FormControl('customerDevice'),
      cardTerminalIP: new FormControl(null),
      cardTerminalPort: new FormControl(8000),
      cardTerminalProtocol: new FormControl('https'),
      cardTerminalPosId: new FormControl(null),
      guestCheckoutEnabled: new FormControl(false),
      guestInactivityMinutes: new FormControl(5, [Validators.required, Validators.min(1), Validators.max(60)]),
      nfcReaderEnabled: new FormControl(false),
      nfcReaderMode: new FormControl('hid'),
      ageVerificationMethod: new FormControl('hit2open'),
      useProxy: new FormControl(false),
      proxyProtocol: new FormControl('http'),
      proxyIP: new FormControl(null),
      proxyPort: new FormControl(9500),
      useCloudTerminalApi: new FormControl(false),
      vivaTerminalId: new FormControl(null),
      vivaMerchantIdPOS: new FormControl(null),
      vivaApiKeyPOS: new FormControl(null),
      maxCardPaymentAmount: new FormControl(null),
      smartTillEnabled: new FormControl(false),
      smartTillProxyHost: new FormControl('127.0.0.1'),
      smartTillProxyPort: new FormControl(9500),
      smartTillTillNo: new FormControl(null),
      smartTillCashierNo: new FormControl(null),
      smartTillRequireForCashPay: new FormControl(true),
    });

    this.terminalForm.get('smartTillEnabled')?.valueChanges.subscribe(() => {
      this.applySmartTillValidators();
    });
    this.applySmartTillValidators();
  }

  private applySmartTillValidators(): void {
    const enabled = this.terminalForm.get('smartTillEnabled')?.value === true;
    const host = this.terminalForm.get('smartTillProxyHost');
    const port = this.terminalForm.get('smartTillProxyPort');
    const till = this.terminalForm.get('smartTillTillNo');
    const cashier = this.terminalForm.get('smartTillCashierNo');

    if (enabled) {
      host?.setValidators([Validators.required]);
      port?.setValidators([Validators.required, Validators.min(1), Validators.max(65535)]);
      till?.setValidators([Validators.required, Validators.pattern(/^\d{6}$/)]);
      cashier?.setValidators([Validators.required, Validators.pattern(/^\d{8}$/)]);
    } else {
      host?.clearValidators();
      port?.clearValidators();
      till?.clearValidators();
      cashier?.clearValidators();
    }

    host?.updateValueAndValidity();
    port?.updateValueAndValidity();
    till?.updateValueAndValidity();
    cashier?.updateValueAndValidity();
  }

  loadFormData(): void {
    this.spinner.show();

    if (this.isEditMode) {
      forkJoin({
        users: this.posTerminalsService.getTerminalUsers(this.storeId),
        terminals: this.posTerminalsService.getTerminals(this.storeId),
      }).subscribe({
        next: ({ users, terminals }: any) => {
          this.assignableUsers = users.response?.data?.users || [];
          this.applyTerminalFromList(terminals);
          this.spinner.hide();
        },
        error: (err: any) => {
          this.toastr.error(err.error?.response?.message || 'Failed to load terminal data');
          this.spinner.hide();
          this.goBack();
        },
      });
    } else {
      this.posTerminalsService.getTerminalUsers(this.storeId).subscribe({
        next: (res: any) => {
          this.assignableUsers = res.response?.data?.users || [];
          this.spinner.hide();
        },
        error: (err: any) => {
          this.toastr.error(err.error?.response?.message || 'Failed to load assignable users');
          this.spinner.hide();
        },
      });
    }
  }

  applyTerminalFromList(res: any): void {
    const terminals: any[] = res.response?.data?.terminals || [];
    const terminal = terminals.find((t: any) => t._id === this.terminalId);
    if (terminal) {
      const assignedId = terminal.assignedUserId?._id ?? null;
      this.originalAssignedUserId = assignedId;
      this.terminalForm.patchValue({
        ...terminal,
        ageVerificationMethod: terminal.ageVerificationMethod || 'hit2open',
        productScanQrMode: terminal.productScanQrMode ?? 'warn',
        assignedUserId: assignedId,
        smartTillEnabled: terminal.smartTillEnabled ?? false,
        smartTillProxyHost: terminal.smartTillProxyHost ?? '127.0.0.1',
        smartTillProxyPort: terminal.smartTillProxyPort ?? 9500,
        smartTillTillNo: terminal.smartTillTillNo ?? null,
        smartTillCashierNo: terminal.smartTillCashierNo ?? null,
        smartTillRequireForCashPay: terminal.smartTillRequireForCashPay ?? true,
      });
      this.applySmartTillValidators();
    } else {
      this.toastr.error('Terminal not found');
      this.goBack();
    }
  }

  isUserOptionDisabled(user: any): boolean {
    if (!user.assignedTerminalId) {
      return false;
    }
    return user.assignedTerminalId !== this.terminalId;
  }

  userOptionLabel(user: any): string {
    const base = `${user.name} (${user.email})`;
    if (this.isUserOptionDisabled(user)) {
      return `${base} — assigned to ${user.assignedTerminalName}`;
    }
    return base;
  }

  get showPrinterFields(): boolean {
    return this.terminalForm.get('receiptPrinterEnabled')?.value === true;
  }

  get showCardTerminalFields(): boolean {
    return this.terminalForm.get('preferredPaymentMethod')?.value === 'cardTerminal';
  }

  get showNfcModeField(): boolean {
    return this.terminalForm.get('nfcReaderEnabled')?.value === true;
  }

  get showProxyFields(): boolean {
    return this.terminalForm.get('useProxy')?.value === true;
  }

  get showSmartTillFields(): boolean {
    return this.terminalForm.get('smartTillEnabled')?.value === true;
  }

  get useCloudTerminalApi(): boolean {
    return this.terminalForm.get('useCloudTerminalApi')?.value === true;
  }

  onSubmit(): void {
    if (this.terminalForm.invalid) {
      this.terminalForm.markAllAsTouched();
      this.toastr.error('Please fill in all required fields');
      return;
    }

    const formVal = this.terminalForm.value;
    const assignedUserId: string | null = formVal.assignedUserId || null;
    const assignmentChanged =
      this.isEditMode && assignedUserId !== this.originalAssignedUserId;

    if (assignmentChanged && assignedUserId !== null) {
      Swal.fire({
        title: 'Change assigned login?',
        text:
          'Any active POS session on this terminal (and the selected manager\'s session on any other terminal) will be ended. The POS must log in again.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          this.submitForm(formVal, assignedUserId);
        }
      });
      return;
    }

    this.submitForm(formVal, assignedUserId);
  }

  private submitForm(formVal: any, assignedUserId: string | null): void {
    const { assignedUserId: _ignored, ...rest } = formVal;
    const smartTillEnabled = formVal.smartTillEnabled === true;
    const tillTrimmed = String(formVal.smartTillTillNo ?? '').trim();
    const cashierTrimmed = String(formVal.smartTillCashierNo ?? '').trim();
    const proxyHostTrimmed = String(formVal.smartTillProxyHost ?? '127.0.0.1').trim();
    const payload: any = {
      ...rest,
      guestInactivityMinutes: Math.round(Number(formVal.guestInactivityMinutes) || 5),
      maxCardPaymentAmount:
        formVal.maxCardPaymentAmount !== '' && formVal.maxCardPaymentAmount !== null
          ? Number(formVal.maxCardPaymentAmount)
          : null,
      smartTillEnabled,
      smartTillProxyHost: proxyHostTrimmed || '127.0.0.1',
      smartTillProxyPort: Math.round(Number(formVal.smartTillProxyPort)) || 9500,
      smartTillRequireForCashPay: formVal.smartTillRequireForCashPay ?? true,
      smartTillTillNo: /^\d{6}$/.test(tillTrimmed) ? tillTrimmed : null,
      smartTillCashierNo: /^\d{8}$/.test(cashierTrimmed) ? cashierTrimmed : null,
    };

    if (this.isEditMode) {
      if (assignedUserId !== this.originalAssignedUserId) {
        payload.assignedUserId = assignedUserId;
      }
    } else {
      payload.assignedUserId = assignedUserId;
    }

    this.spinner.show();

    if (this.isEditMode) {
      this.posTerminalsService.updateTerminal(this.storeId, this.terminalId, payload).subscribe({
        next: (res: any) => {
          const endedSessionCount = res.response?.data?.endedSessionCount ?? 0;
          this.toastr.success('Terminal updated successfully');
          if (endedSessionCount > 0) {
            this.toastr.info(`${endedSessionCount} active POS session(s) ended.`);
          }
          this.spinner.hide();
          this.goBack();
        },
        error: (err: any) => {
          this.toastr.error(err.error?.response?.message || 'Failed to update terminal');
          this.spinner.hide();
        },
      });
    } else {
      this.posTerminalsService.createTerminal(this.storeId, payload).subscribe({
        next: () => {
          this.toastr.success('Terminal created successfully');
          this.spinner.hide();
          this.goBack();
        },
        error: (err: any) => {
          this.toastr.error(err.error?.response?.message || 'Failed to create terminal');
          this.spinner.hide();
        },
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/store', this.storeId, 'pos-terminals']);
  }
}
